import { Box, type Theme } from "@mui/material";
import { useEffect, useRef } from "react";

const ITEM_HEIGHT = 48;
const MAX_VISIBLE_ITEMS = 5;
const LOOP_REPETITION_TARGET = 300;
const HAS_SCROLL_END =
  typeof document !== "undefined" && "onscrollend" in document.createElement("div");

interface WheelColumnItem {
  value: number | string;
  label: string;
  ariaLabel: string;
}

interface WheelColumnProps {
  name: string;
  items: WheelColumnItem[];
  selectedValue: number | string;
  onSelect: (value: number | string) => void;
  onScrollingChange?: (isScrolling: boolean) => void;
  loop?: boolean;
}

/**
 * Returns an odd repetition count so there is always a true middle repetition.
 */
function getRepetitionCount(itemCount: number): number {
  const count = Math.max(3, Math.ceil(LOOP_REPETITION_TARGET / itemCount));
  return count % 2 === 0 ? count + 1 : count;
}

export function WheelColumn(props: WheelColumnProps) {
  const { name, items, selectedValue, onSelect, onScrollingChange, loop = false } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const overlayContentRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const isScrollingRef = useRef(false);
  const onScrollingChangeRef = useRef(onScrollingChange);
  onScrollingChangeRef.current = onScrollingChange;

  const repetitions = loop ? getRepetitionCount(items.length) : 1;
  const middleRepetition = Math.floor(repetitions / 2);
  const spacerHeight = ((MAX_VISIBLE_ITEMS - 1) / 2) * ITEM_HEIGHT;

  function scrollToIndex(index: number, behavior: ScrollBehavior = "instant"): void {
    const container = containerRef.current;
    if (container?.scrollTo) {
      container.scrollTo({ top: index * ITEM_HEIGHT, behavior });
    }
  }

  function syncOverlay(): void {
    const container = containerRef.current;
    const overlayContent = overlayContentRef.current;
    if (container && overlayContent) {
      overlayContent.style.transform = `translateY(${-container.scrollTop}px)`;
    }
  }

  /**
   * After scroll settles, silently jump back to the middle repetition
   * so the user never reaches the edge.
   */
  function recenterToMiddle(itemIndex: number): void {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const middleIndex = middleRepetition * items.length + itemIndex;
    const targetTop = middleIndex * ITEM_HEIGHT;

    if (Math.abs(container.scrollTop - targetTop) > ITEM_HEIGHT) {
      container.scrollTo({ top: targetTop, behavior: "instant" });
      syncOverlay();
    }
  }

  function selectCenteredItem(): void {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const rawIndex = Math.round(container.scrollTop / ITEM_HEIGHT);

    if (loop) {
      // Double-modulo to handle negative values (JS % can return negatives)
      const itemIndex = ((rawIndex % items.length) + items.length) % items.length;
      const item = items[itemIndex];
      if (item) {
        onSelectRef.current(item.value);
      }

      recenterToMiddle(itemIndex);
    } else {
      const clampedIndex = Math.max(0, Math.min(rawIndex, items.length - 1));
      const item = items[clampedIndex];
      if (item) {
        onSelectRef.current(item.value);
      }
    }

    if (isScrollingRef.current) {
      isScrollingRef.current = false;
      onScrollingChangeRef.current?.(false);
    }
  }

  // Scroll to initial selection + register scrollend listener + cleanup
  useEffect(() => {
    const container = containerRef.current;

    const itemIndex = items.findIndex((item) => item.value === selectedValue);
    if (itemIndex !== -1) {
      const startIndex = loop ? middleRepetition * items.length + itemIndex : itemIndex;
      scrollToIndex(startIndex, "instant");
    }

    syncOverlay();

    if (container && "onscrollend" in container) {
      const handler = (): void => {
        selectCenteredItem();
      };

      container.addEventListener("scrollend", handler);
      return () => {
        container.removeEventListener("scrollend", handler);
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
      };
    }

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScroll = (): void => {
    syncOverlay();

    if (!isScrollingRef.current) {
      isScrollingRef.current = true;
      onScrollingChangeRef.current?.(true);
    }

    if (HAS_SCROLL_END) {
      return;
    }

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      selectCenteredItem();
    }, 50);
  };

  const handleItemClick = (flatIndex: number, value: number | string): void => {
    scrollToIndex(flatIndex, "smooth");
    onSelect(value);
  };

  return (
    <Box sx={{ position: "relative", minWidth: 64 }}>
      {/* Scrollable items */}
      <Box
        ref={containerRef}
        role="listbox"
        aria-label={`Select ${name}`}
        sx={{
          height: MAX_VISIBLE_ITEMS * ITEM_HEIGHT,
          overflowY: "auto",
          scrollSnapType: "y mandatory",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
        onScroll={handleScroll}
      >
        <Box sx={{ height: spacerHeight, flexShrink: 0 }} />

        {Array.from({ length: repetitions }, (_, repetitionIndex) =>
          items.map((item, itemIndex) => {
            const flatIndex = repetitionIndex * items.length + itemIndex;
            const isMiddleRepetition = repetitionIndex === middleRepetition;

            return (
              <Box
                key={`${repetitionIndex}-${item.value}`}
                {...(isMiddleRepetition
                  ? {
                      role: "option",
                      "aria-label": item.ariaLabel,
                      "aria-selected": item.value === selectedValue,
                    }
                  : { "aria-hidden": true })}
                sx={(theme: Theme) => ({
                  height: ITEM_HEIGHT,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  scrollSnapAlign: "center",
                  cursor: "pointer",
                  userSelect: "none",
                  fontSize: "1rem",
                  color: theme.palette.text.secondary,
                })}
                onClick={() => {
                  handleItemClick(flatIndex, item.value);
                }}
              >
                {item.label}
              </Box>
            );
          })
        )}

        <Box sx={{ height: spacerHeight, flexShrink: 0 }} />
      </Box>

      {/* Fixed circle at center — clips a duplicate white-text layer to the indicator area */}
      <Box
        sx={(theme) => ({
          position: "absolute",
          top: spacerHeight,
          left: "50%",
          transform: "translateX(-50%)",
          width: ITEM_HEIGHT,
          height: ITEM_HEIGHT,
          borderRadius: "50%",
          backgroundColor: theme.palette.primary.main,
          overflow: "hidden",
          pointerEvents: "none",
        })}
      >
        <Box ref={overlayContentRef} aria-hidden="true" sx={{ willChange: "transform" }}>
          {Array.from({ length: repetitions }, (_, repetitionIndex) =>
            items.map((item) => (
              <Box
                key={`overlay-${repetitionIndex}-${item.value}`}
                sx={(theme: Theme) => ({
                  height: ITEM_HEIGHT,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1rem",
                  fontWeight: 600,
                  color: theme.palette.primary.contrastText,
                })}
              >
                {item.label}
              </Box>
            ))
          )}
        </Box>
      </Box>
    </Box>
  );
}
